import { useEffect, useState } from 'react'
import { ensureAnonSession } from '../lib/api/tv'
import { fetchWordTvRoom, type WordTvRoom } from '../lib/api/wordTv'
import { readError } from '../lib/errors'
import { useT } from '../i18n'
import { RoomQR } from '../components/RoomQR'

/**
 * The big screen for endless FAKE OUT / CLOSE ENOUGH rooms.
 *
 * Reads through the word_endless_room RPC on a timer rather than
 * subscribing: the endless tables have RLS on with zero policies, so a
 * realtime subscription would deliver silence rather than an error —
 * the counters would simply sit at zero and look broken.
 *
 * Mid-round the TV shows exactly what the table is allowed to know:
 * who has finished and what they scored. No words, no answers.
 *
 * The TV signs in anonymously and never takes a seat. That matters for
 * more than tidiness: seats are charged against the 10-room cap, so a
 * display that seated itself would bill a room to a throwaway account
 * and surface the game in that account's Jump Back In.
 */

const META = {
  fake_out:     { title: 'FAKE OUT',     accent: '#6B8A34', bg: '#EEF4E2' },
  close_enough: { title: 'CLOSE ENOUGH', accent: '#A8A020', bg: '#F6F6DC' },
} as const

export function WordTvApp() {
  const t = useT()
  // Parsed at render from the path, matching the other TV surfaces:
  // /tv/fake-out/ABCDEF
  const parts = window.location.pathname.split('/').filter(Boolean)
  const slug = parts[1] ?? ''
  const code = (parts[2] ?? '').toUpperCase()
  const game = slug === 'close-enough' ? 'close_enough' : 'fake_out'
  const meta = META[game]

  const [ready, setReady] = useState(false)
  const [room, setRoom] = useState<WordTvRoom | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    ensureAnonSession()
      .then(() => { if (!cancelled) setReady(true) })
      .catch(e => { if (!cancelled) setError(readError(e)) })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (!ready || !code) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout>

    const tick = async () => {
      try {
        const r = await fetchWordTvRoom(code)
        if (!cancelled) { setRoom(r); setError(null) }
      } catch (e) {
        if (!cancelled) setError(readError(e))
      }
      if (!cancelled) timer = setTimeout(tick, 2500)
    }
    void tick()
    return () => { cancelled = true; clearTimeout(timer) }
  }, [ready, code])

  if (!code) return <TvMessage title={t('Enter a room code')} body={t('Open this page with a code, like /tv/fake-out/ABCDEF')} />
  if (error) return <TvMessage title={t('Couldn’t load the room')} body={error} />
  if (!ready || !room) return <TvMessage title={t('Starting up…')} body={t('Connecting.')} />
  if (room.status === 'abandoned') return <TvMessage title={t('Party’s over')} body={t('The host closed this room.')} />

  const joined = room.seats.filter(s => s.joined)
  const ranked = [...room.seats].filter(s => s.joined)
    .sort((a, b) => (b.score ?? -1) - (a.score ?? -1))
  const finished = room.status === 'finished'
  const doneTarget = game === 'fake_out' ? 6 : 1
  // Rooms now seat up to 16 — past 8 the roster wraps to two columns
  // so the code never gets pushed off a 16:9 screen.
  const twoCol = joined.length > 8

  return (
    <div className="h-screen w-screen overflow-hidden flex"
         style={{ backgroundColor: meta.bg }}>
      <div className="w-[42%] shrink-0 flex flex-col items-center justify-center px-10 text-center">
        <h1 className="font-display text-6xl font-black tracking-wide" style={{ color: meta.accent }}>
          {meta.title}
        </h1>

        {finished ? (
          <p className="font-display text-3xl text-brand-muted uppercase tracking-[0.3em] mt-8">
            {t('Final standings')}
          </p>
        ) : (
          <>
            <p className="text-sm font-semibold uppercase tracking-[0.4em] text-brand-muted mt-10 mb-2">
              {t('Room code')}
            </p>
            <p className="font-display font-black text-[7rem] leading-none tracking-[0.12em] text-brand-text">
              {room.code}
            </p>
            <div className="mt-8">
              <RoomQR code={room.code} size={168} />
            </div>
            <p className="font-display text-xl text-brand-text mt-6">
              {t('On your phone, go to')}{' '}
              <span className="font-mono font-bold" style={{ color: meta.accent }}>
                games.jordandraper.com/join
              </span>
            </p>
            <p className="font-display text-base text-brand-muted mt-2">
              {t('No account needed. Guests can play.')}
            </p>
          </>
        )}
      </div>

      <div className="flex-1 min-w-0 flex flex-col justify-center px-10 py-10">
        <p className="font-display text-xl text-brand-muted uppercase tracking-[0.3em] mb-5">
          {finished
            ? t('{n} played', { n: joined.length })
            : t('{n} of {total} joined', { n: joined.length, total: room.seats.length })}
        </p>

        <div className={twoCol ? 'grid grid-cols-2 gap-2.5 min-h-0' : 'flex flex-col gap-3 min-h-0'}>
          {(finished ? ranked : joined).map((s, i) => (
            <div key={s.player}
                 className={`flex items-center bg-white rounded-2xl shadow-card
                             ${twoCol ? 'gap-3 px-4 py-2' : 'gap-5 px-6 py-3'}`}>
              {finished && (
                <span className={`font-display font-black text-center shrink-0
                                  ${twoCol ? 'text-xl w-7' : 'text-3xl w-10'}`}
                      style={{ color: i === 0 ? meta.accent : '#9CA3AF' }}>
                  {i + 1}
                </span>
              )}
              <span className={`flex-1 min-w-0 font-display font-bold text-brand-text truncate
                                ${twoCol ? 'text-xl' : 'text-3xl'}`}>
                {s.name}
              </span>
              {!finished && (
                <span className={`shrink-0 font-semibold text-brand-muted
                                  ${twoCol ? 'text-sm' : 'text-lg'}`}>
                  {s.done >= doneTarget ? '✓' : `${s.done}/${doneTarget}`}
                </span>
              )}
              <span className={`font-display font-black shrink-0 ${twoCol ? 'text-xl' : 'text-3xl'}`}
                    style={{ color: meta.accent }}>
                {s.score ?? 0}
              </span>
            </div>
          ))}
          {joined.length === 0 && (
            <p className="font-display text-2xl text-brand-muted">
              {t('No one’s here yet. Share the code above.')}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

function TvMessage({ title, body }: { title: string; body: string }) {
  return (
    <div className="h-screen w-screen overflow-hidden flex flex-col items-center justify-center bg-brand-bg p-16 text-center">
      <h1 className="font-display text-6xl font-black text-brand-text">{title}</h1>
      <p className="font-display text-3xl text-brand-muted mt-6">{body}</p>
    </div>
  )
}
